import { useState, useCallback, useEffect } from "react";
import { toAmount } from "features/groups";
import { useUserSession } from "shared/contexts/UserContext";
import { Transaction } from "@/types/Transaction";
import { BudgetTransaction } from "@/types/BudgetTransaction";
import { createBudgetTransactionId } from "features/budget/components/transactions/budget-transactions/personalTransactionUtils";

// -----------------------------------------------------------------------------
//  Locals
// -----------------------------------------------------------------------------

export interface UseAddBudgetTransactionResult {
    username: string;
    newTransaction: Transaction;
    validationError: string;
    setField: <K extends keyof Transaction>(
        key: K,
        value: Transaction[K]
    ) => void;
    buildTransaction: () => BudgetTransaction;
    reset: () => void;
}

export interface UseAddBudgetTransactionOptions {
    /** When set (e.g. calendar day), draft date syncs to this instant (start of local day). */
    defaultDate?: Date | null;
}

// -----------------------------------------------------------------------------
//  Hooks
// -----------------------------------------------------------------------------

export function useAddBudgetTransaction(
    options?: UseAddBudgetTransactionOptions,
): UseAddBudgetTransactionResult {
    const { profile } = useUserSession();
    const username: string = profile?.username ?? "";

    const [validationError, setValidationError] = useState<string>("");
    const [newTransaction, setNewTransaction] = useState<Transaction>({
        _id: "",
        payer: username,
        date: Date.now().toString(),
        name: "",
        currency: "ARS",
        amount: 0,
        type: "expense",
        splitDetails: {}
    });

    useEffect(() => {
        const d = options?.defaultDate;
        if (d == null || isNaN(d.getTime())) return;
        setNewTransaction((prev) => ({
            ...prev,
            date: d.getTime().toString(),
        }));
    }, [options?.defaultDate]);

    //! Type-safe field setter for the draft.
    const setField = useCallback<
        <K extends keyof Transaction>(
            key: K,
            value: Transaction[K]
        ) => void
    >((key, value) => {
        setNewTransaction((previousTransaction) => ({
            ...previousTransaction,
            [key]: value,
        }));
    }, []);

    //! Produces the final transaction ready to be sent to the backend.
    const buildTransaction = useCallback((): BudgetTransaction => {
        const normalizedAmount: number = toAmount(newTransaction.amount);

        const tx: BudgetTransaction = {
            _id: createBudgetTransactionId(),
            payer: username,
            date: newTransaction.date || Date.now().toString(),
            name: newTransaction.name,
            currency: newTransaction.currency,
            amount: normalizedAmount,
            type: newTransaction.type ?? "expense",
            splitDetails: {},
        } as BudgetTransaction;
        if (newTransaction.liquidityPoolId) tx.liquidityPoolId = newTransaction.liquidityPoolId;
        if (newTransaction.category?.trim()) tx.category = newTransaction.category.trim();
        return tx;
    }, [newTransaction, username]);

    //! Resets local form state and clears validation errors
    const reset = useCallback((): void => {
        const d = options?.defaultDate;
        const dateStr =
            d != null && !isNaN(d.getTime())
                ? d.getTime().toString()
                : Date.now().toString();
        setNewTransaction({
            _id: "",
            payer: username,
            date: dateStr,
            name: "",
            currency: "ARS",
            amount: 0,
            type: "expense",
            splitDetails: {}
        });
        setValidationError("");
    }, [username, options?.defaultDate]);

    return {
        username,
        newTransaction,
        validationError,
        setField,
        buildTransaction,
        reset,
    };
}
